import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetVehicleSaleReportQuery } from '../../../../api/endpoints/vehicleSaleReport';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';

// Frontend alan adi -> Backend kolon adi (DTO property adlari)
export const backendColumnMap = {
    dealerName: 'DealerName',
    chassisNo: 'ChassisNo',
    modelInfo: 'ModelInfo',
    customerCity: 'CustomerCity',
    customerDetail: 'CustomerDetail',
    customerAddress: 'CustomerAddress',
    saleDate: 'SaleDate'
};

// Dropdown (kategorik) filtre uygulanan alanlar
export const DROPDOWN_FIELDS = ['dealerName', 'modelInfo', 'customerCity'];

// Metinsel filtre uygulanan alanlar
export const TEXT_FIELDS = ['chassisNo', 'customerDetail', 'customerAddress'];

// includeFilterOptions ile backend'den deger istenen kolonlar
const FILTER_OPTIONS_COLUMNS = DROPDOWN_FIELDS.map((field) => backendColumnMap[field]);

const OPERATOR_EQUALS = 5;

const INITIAL_FILTER_STATE = {
    chassisNo: null,
    chassisNoMatchMode: null,
    customerDetail: null,
    customerDetailMatchMode: null,
    customerAddress: null,
    customerAddressMatchMode: null,
    dealerName: null,
    modelInfo: null,
    customerCity: null
};

const getOperatorValue = (matchMode) => matchModeToOperator[matchMode] || matchModeToOperator[FilterMatchMode.CONTAINS];

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    TEXT_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (value === null || value === undefined || String(value).trim() === '') return;
        filters.push({
            column: backendColumnMap[field],
            operator: getOperatorValue(filtersState?.[`${field}MatchMode`]),
            value: String(value).trim()
        });
    });

    DROPDOWN_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (value === null || value === undefined || value === '') return;
        filters.push({
            column: backendColumnMap[field],
            operator: OPERATOR_EQUALS,
            value: String(value)
        });
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = backendColumnMap[field];
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

export default function useVehicleSaleReport() {
    const initialLazyParams = { first: 0, rows: 10, page: 1 };
    const { getCurrentLocale, getCurrentLanguageId } = useLanguage();

    const [lazyParams, setLazyParams] = useState(initialLazyParams);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [filterOptions, setFilterOptions] = useState(null);
    const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
    const [dealerBranchCode, setDealerBranchCode] = useState(null);

    const queryParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null,
            includeFilterOptions: !filterOptionsLoaded,
            filterOptionsColumns: !filterOptionsLoaded ? FILTER_OPTIONS_COLUMNS : null,
            languageId: getCurrentLanguageId(),
            // dealerBranchCode = secilen bayinin sube/BOS kodu (sapDealerId)
            dealerBranchCode: dealerBranchCode || null
        };
    }, [lazyParams.page, lazyParams.rows, activeFilters, sortField, sortOrder, filterOptionsLoaded, dealerBranchCode, getCurrentLanguageId]);

    const exportParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            languageId: getCurrentLanguageId(),
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null,
            dealerBranchCode: dealerBranchCode || null,
            compressionType: 0
        };
    }, [activeFilters, sortField, sortOrder, dealerBranchCode, getCurrentLanguageId]);

    const { data, isFetching, isError, error } = useGetVehicleSaleReportQuery(queryParams);

    useEffect(() => {
        if (!filterOptionsLoaded && data?.filterOptions) {
            setFilterOptions(data.filterOptions);
            setFilterOptionsLoaded(true);
        }
    }, [data, filterOptionsLoaded]);

    const items = data ? (Array.isArray(data.items) ? data.items : []) : [];
    const totalCount = data ? data.totalCount ?? 0 : 0;

    const handleFilterChange = useCallback((updatedFilters) => {
        setActiveFilters((prev) => ({ ...prev, ...updatedFilters }));
        setLazyParams(initialLazyParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetFilters = useCallback(() => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setDealerBranchCode(null);
        setLazyParams(initialLazyParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSortChange = useCallback((event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;
        const backendColumn = backendColumnMap[nextSortField];

        if (!backendColumn && nextSortOrder !== 0) return;

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        setLazyParams(initialLazyParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onPage = useCallback((e) => {
        setLazyParams({ first: e.first, rows: e.rows, page: e.page + 1 });
    }, []);

    const handleDealerBranchCodeChange = useCallback((value) => {
        setDealerBranchCode(value || null);
        setLazyParams(initialLazyParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        items,
        totalCount,
        isFetching,
        isError,
        error,
        lazyParams,
        activeFilters,
        filterOptions,
        locale: getCurrentLocale(),
        handleFilterChange,
        resetFilters,
        onPage,
        sortField,
        sortOrder,
        onSort: handleSortChange,
        exportParams,
        dealerBranchCode,
        onDealerBranchCodeChange: handleDealerBranchCodeChange
    };
}
